import { createSelector } from 'reselect';
import { values } from 'lodash';

import { utils, scan as constants, params as paramConstants } from './../helpers';
import { jiraScansActionTypes } from './../actions/types';
import { getJobs } from './jobs';
import { paramsByJobIds } from './params';

const scans = (state = { byId: {}}, action) => {
  switch (action.type) {
    case jiraScansActionTypes.success:
      return { byId: { ...state.byId, ...action.response }};
    default:
      return state;
  }
};
export default scans;

const getScanState = state => state.scans.byId;
export const getScanById = (state, id) => getScanState(state)[id];
export const getScans = (state) => values(getScanState(state));

const scansByKey = createSelector(
  getScans,
  (scans) => utils.propertyReducer(scans, s => s.key, s => s)
);
export const getScansByKey = (state, key) => scansByKey(state)[key];

const scansByDefPath = createSelector(
  getScans,
  (scans) => utils.propertyReducer(scans, s => s.defPath, s => s)
);
export const getScansByDefPath = (state, defPath) => scansByDefPath(state)[defPath];

const scansByImagePath = createSelector(
  getScans,
  (scans) => utils.propertyReducer(scans, s => s.imagePath, s => s)
);
export const getScansByImagePath = (state, imagePath) =>
  scansByImagePath(state)[imagePath];

const scansByImageName = createSelector(
  getScans,
  (scans) => utils.propertyReducer(scans, s => constants.getImageName(s), s => s)
);
export const getScansByImageName = (state, imageName) =>
  scansByImageName(state)[imageName];

export const getShowOnReleaseMetricsScanImages = createSelector(
  getScans,
  (scans) => utils.accumulatorReducerByFunc(
    scans,
    s => s.showOnReleaseMetrics ? 'Show' : null,
    s => {
      const name = s.defPath || s.imagePath;
      return name.slice(
        name.lastIndexOf('\\') + 1,
        name.lastIndexOf('.'))
        .toLowerCase();
    }
  )['Show']
);

const getByJob = (
  job,
  paramsByJobIds,
  scansByKey,
  scansByImagePath,
  scansByDefPath,
  scansByImageName
) => {
  //steps for finding the correct scan corresponding to the image
  //1. try using scan issue key
  //2. then try using the scan def path from the JIRA ticket:
  //  this is a relative path to the scan stored within code/tools/scan-defs,
  //  this most often maps to the 'IMAGE_PATH' variable from the job's params
  //3. then try using image path from the JIRA ticket:
  //  this is an absolute path to the scan stored at a network location
  //  still maps to 'IMAGE_PATH' for some old scans
  //4. then try using image name
  //5. finally use a hardcoded list of exceptions
  const params = paramsByJobIds[job.id];
  if (!params) {
    return undefined;
  }

  const scanName = params[paramConstants.IMAGEFILE];
  if (!scanName) {
    return undefined;
  }

  const keyIndex = scanName.toLowerCase().indexOf('scan');
  const keyEndIndex = scanName.indexOf('_', keyIndex + 1);
  let scanKey = keyEndIndex >= 0 ?
    scanName.substring(keyIndex, keyEndIndex) :
    scanName;
  scanKey = scanKey.toUpperCase();

  const payloadAssignmentAttempts = [
    () => scansByKey[scanKey],
    () => scansByDefPath[params[paramConstants.JENKINS_IMAGE_PATH]],
    () => scansByImagePath[params[paramConstants.JENKINS_IMAGE_PATH]],
    () => scansByImageName[params[paramConstants.IMAGEFILE]],
    () => {
      const key = constants.IMAGE_TO_KEY_LIST[params[paramConstants.IMAGEFILE]];
      if (!key) {
        return undefined;
      }

      return scansByKey[key];
    }
  ];

  for (let i = 0; i < payloadAssignmentAttempts.length; ++i) {
    const payload = payloadAssignmentAttempts[i]();
    if (payload) {
      return payload;
    }
  }

  return undefined;
};

export const scansByJobId = createSelector(
  getJobs,
  paramsByJobIds,
  scansByKey,
  scansByImagePath,
  scansByDefPath,
  scansByImageName,
  (
    jobs,
    paramsByJobIds,
    scansByKey,
    scansByImagePath,
    scansByDefPath,
    scansByImageName
  ) => utils.propertyReducer(
    jobs,
    j => j.id,
    j => getByJob(
      j,
      paramsByJobIds,
      scansByKey,
      scansByImagePath,
      scansByDefPath,
      scansByImageName
    ))
);
export const getScanByJobId = (state, jobId) => scansByJobId(state)[jobId];


// WEBPACK FOOTER //
// src/reducers/scans.js